"use client";

import { useState, useEffect, useCallback } from "react";
import { getAtlas } from "@/lib/atlas/integration";
import type {
  AtlasNavigationResult,
  AtlasSearchProcess,
} from "@/lib/atlas/integration";

export function useMindalooAtlas(chatWindowId = "mindaloo-main") {
  const [atlasProcess, setAtlasProcess] = useState<AtlasSearchProcess | null>(null);
  const [atlasNavigationResult, setAtlasNavigationResult] =
    useState<AtlasNavigationResult | null>(null);
  const [atlasHistory, setAtlasHistory] = useState<
    Array<{
      process: AtlasSearchProcess;
      result: AtlasNavigationResult;
      messageId: string;
      timestamp: number;
      outcome?: "accepted" | "corrected" | "rejected";
    }>
  >([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    getAtlas()
      .initialize()
      .then(() => {
        setAtlasProcess({
          status: "ready",
          message: "Atlas cognitive map ready",
        });
      })
      .catch(() => {
        setAtlasProcess({
          status: "ready",
          message: "Atlas ready",
        });
      });
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(`atlas_history_${chatWindowId}`);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      setAtlasHistory(parsed.history || []);
      if (parsed.lastResult) setAtlasNavigationResult(parsed.lastResult);
    } catch {
      /* ignore */
    }
  }, [chatWindowId]);

  const persistHistory = useCallback(
    (
      history: typeof atlasHistory,
      lastResult: AtlasNavigationResult | null
    ) => {
      localStorage.setItem(
        `atlas_history_${chatWindowId}`,
        JSON.stringify({ history, lastResult })
      );
    },
    [chatWindowId]
  );

  const navigateForMessage = useCallback(
    async (message: string, messageId: string) => {
      const atlas = getAtlas();
      await atlas.initialize();

      // Computed up front (not after getSearchProcess) so both calls share
      // identical context and navigate() can reuse getSearchProcess's fetch
      // instead of hitting /api/atlas/navigate a second time.
      const previousNavigations = atlasHistory
        .map((h) => h.result?.location?.topicName)
        .filter(Boolean) as string[];
      const navContext = {
        currentTopic: atlasNavigationResult?.location?.topicName,
        previousNavigations,
      };

      const process = await atlas.getSearchProcess(message, (p) => {
        setAtlasProcess(p);
      }, navContext);

      if (process.status === "skipped") {
        setAtlasProcess({
          status: "ready",
          message: "Atlas cognitive map ready",
          currentLocation: atlasNavigationResult?.location?.topicName,
        });
        return { process, result: atlasNavigationResult };
      }

      const result = await atlas.navigate(message, navContext);

      setAtlasNavigationResult(result);
      const readyProcess: AtlasSearchProcess = {
        ...process,
        status: "ready",
        message: `Navigated to ${result.location?.topicName || "cognitive space"}`,
        currentLocation: result.location?.topicName,
        nearbyAreas:
          result.orientation?.nearbyTopics?.map((t) => t.name) || [],
      };
      setAtlasProcess(readyProcess);

      const entry = {
        process: readyProcess,
        result,
        messageId,
        timestamp: Date.now(),
      };
      setAtlasHistory((prev) => {
        const next = [...prev, entry];
        persistHistory(next, result);
        return next;
      });

      return { process: readyProcess, result };
    },
    [atlasHistory, atlasNavigationResult, persistHistory]
  );

  const recordOutcome = useCallback((messageId: string, outcome: "accepted" | "corrected" | "rejected") => {
    const result = atlasHistory.find((entry) => entry.messageId === messageId)?.result ?? atlasNavigationResult;
    if (result) {
      void fetch("/api/atlas/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          evidenceIds: result.evidenceLedger?.map((evidence) => evidence.id) ?? [],
          trustScore: result.trustReport?.overall ?? 0,
          outcome,
        }),
      });
    }
    setAtlasHistory((previous) => {
      const next = previous.map((entry) => entry.messageId === messageId ? { ...entry, outcome } : entry);
      persistHistory(next, atlasNavigationResult);
      return next;
    });
  }, [atlasHistory, atlasNavigationResult, persistHistory]);

  return {
    atlasProcess,
    setAtlasProcess,
    atlasNavigationResult,
    atlasHistory,
    navigateForMessage,
    recordOutcome,
  };
}
